import { useState, useEffect, useRef, useCallback } from 'react';

const HIDE_CONTROLS_DELAY_MS = 200;

interface UseControlsVisibilityProps {
	playingState: boolean;
}

export function useControlsVisibility({ playingState }: Readonly<UseControlsVisibilityProps>) {
	const [controlsVisible, setControlsVisible] = useState<boolean>(true);
	const hideControlsTimeoutRef = useRef<number | null>(null);
	const isCursorOverVideoRef = useRef<boolean>(false);
	const playingStateRef = useRef<boolean>(playingState);
	playingStateRef.current = playingState;

	const showControls = useCallback((): void => {
		if (hideControlsTimeoutRef.current) {
			clearTimeout(hideControlsTimeoutRef.current);
			hideControlsTimeoutRef.current = null;
		}
		setControlsVisible(true);
	}, []);

	const scheduleHideControls = useCallback((): void => {
		if (!playingStateRef.current) {
			return;
		}

		if (hideControlsTimeoutRef.current) {
			clearTimeout(hideControlsTimeoutRef.current);
		}

		hideControlsTimeoutRef.current = window.setTimeout(() => {
			setControlsVisible(false);
		}, HIDE_CONTROLS_DELAY_MS);
	}, []);

	const handleVideoMouseEnter = useCallback((): void => {
		isCursorOverVideoRef.current = true;
		showControls();
	}, [showControls]);

	const handleVideoMouseLeave = useCallback((): void => {
		isCursorOverVideoRef.current = false;
		scheduleHideControls();
	}, [scheduleHideControls]);

	useEffect(() => {
		return () => {
			if (hideControlsTimeoutRef.current) {
				clearTimeout(hideControlsTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!playingState) {
			showControls();
			return;
		}

		if (!isCursorOverVideoRef.current) {
			scheduleHideControls();
		}
	}, [playingState, showControls, scheduleHideControls]);

	return {
		controlsVisible,
		showControls,
		handleVideoMouseEnter,
		handleVideoMouseLeave
	};
}
